'use client'

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

import { Modal } from '@/components/molecules/Modal/modal.molecule'

import type { ModalKey, ModalPayloads } from './modal.type'
import { modalRegistry } from './modal.registry'
import { setCloseHandler, setOpenHandler } from './modal.registry.events'

type ModalState =
  | {
      [K in ModalKey]: { key: K; payload: ModalPayloads[K] }
    }[ModalKey]
  | null

type ModalRegistryContextValue = {
  open: <K extends ModalKey>(key: K, payload: ModalPayloads[K]) => void
  close: () => void
}

const ModalRegistryContext = createContext<ModalRegistryContextValue | null>(
  null,
)

const hasModalCancelHandler = (
  payload: unknown,
): payload is { onCancel?: () => void } =>
  Boolean(
    payload &&
    typeof payload === 'object' &&
    'onCancel' in payload &&
    typeof payload.onCancel === 'function',
  )

export function ModalRegistryProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [state, setState] = useState<ModalState>(null)

  const open = useCallback(
    <K extends ModalKey>(key: K, payload: ModalPayloads[K]) => {
      setState({ key, payload } as ModalState)
    },
    [],
  )

  const close = useCallback(() => {
    setState(null)
  }, [])

  const dismissCurrentModal = useCallback(() => {
    try {
      if (hasModalCancelHandler(state?.payload)) {
        state.payload.onCancel?.()
      }
    } catch (error) {
      console.error('Modal dismiss callback failed', error)
    } finally {
      close()
    }
  }, [close, state])

  // expose imperative open/close for non-React callers (e.g., menu functions, listeners)
  React.useEffect(() => {
    setOpenHandler(open)
    setCloseHandler(close)
    return () => {
      setOpenHandler(undefined)
      setCloseHandler(undefined)
    }
  }, [open, close])

  const value = useMemo<ModalRegistryContextValue>(
    () => ({ open, close }),
    [open, close],
  )

  const isOpen = Boolean(state)
  const style = state ? (modalRegistry[state.key].style ?? 'info') : 'info'
  const modalTitle =
    state && typeof state.payload === 'object' && state.payload !== null
      ? (state.payload as Record<string, unknown>).modalTitle
      : undefined

  function renderFromRegistry<K extends ModalKey>(
    key: K,
    payload: ModalPayloads[K],
  ) {
    const { render } = modalRegistry[key]
    return render({ ...payload, hideModal: close })
  }

  return (
    <ModalRegistryContext.Provider value={value}>
      {children}
      <Modal
        isOpen={isOpen}
        setIsOpen={(o) => {
          if (!o) close()
        }}
        onDismiss={dismissCurrentModal}
        style={style}
        title={typeof modalTitle === 'string' ? modalTitle : undefined}
      >
        {state ? renderFromRegistry(state.key, state.payload) : null}
      </Modal>
    </ModalRegistryContext.Provider>
  )
}

export function useModalRegistry() {
  const ctx = useContext(ModalRegistryContext)
  if (!ctx)
    throw new Error(
      'useModalRegistry must be used within ModalRegistryProvider',
    )
  return ctx
}
